/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { GitMerge } from '@saleshandy/icons';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import { OverlayPopover, OverlayTooltip, Placement } from '../../overlay';
import { getBadgeVariant } from './helper';
import { SequenceProgress } from '../../../../../components/sequence/enums';
import {
  SequencePauseReasons,
  sequencePauseMessages,
} from '../../../../../components/sequence/enums/sequence-pause-reasons';
import Badge from '../../atoms/badge';
import StatusBadge from '../status-badge';
import { ProductTour } from '../../../../enums/product-tour';
import {
  getAgencyClient,
  getUserTimeZone,
} from '../../../../utils/user-details';
import { RootState } from '../../../../../store/root-reducer';
import { accessibleOnClick } from '../../../../utils/accessible-on-click';
import { redirectWithoutRefresh } from '../../../../utils/redirect';
import hasPermission from '../../../../utils/access-control/has-permission';
import { Permissions } from '../../../../utils/access-control/enums/permissions';

export type SequenceNameProps = {
  name: string;
  row?: any;
  isSubsequence?: boolean;
};

const SubSequencePopoverContent = ({ subSequences, sequenceId }) => {
  const progressColors = {
    1: '#059669',
    2: '#EF4444',
    3: '#9CA3AF',
  };

  const handleClick = React.useCallback(
    (id) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      redirectWithoutRefresh(
        `/sequence/${sequenceId}/subsequence/${id}/conditions`,
      );
    },
    [sequenceId],
  );

  const canNavigateToSubsequence = hasPermission(Permissions.SUBSEQUENCE_SHOW);

  return (
    <>
      {subSequences?.map((subsequence, index) => (
        <div
          key={index}
          className="subsequence-title-bar"
          style={{
            marginBottom: index !== subSequences?.length - 1 ? '12px' : '0',
          }}
        >
          <span
            className="subsequence-rectangle-border"
            style={{
              backgroundColor: progressColors[subsequence.progress],
            }}
          />

          <span
            {...(canNavigateToSubsequence &&
              accessibleOnClick(handleClick(subsequence.id)))}
            className={`subsequence-title-text ${
              canNavigateToSubsequence ? 'subsequence-title-link' : ''
            }`}
          >
            {subsequence.title}
          </span>
        </div>
      ))}
    </>
  );
};

const SequenceNameJSX = ({
  name,
  row,
  isSubsequence = false,
}: SequenceNameProps) => {
  const [showPopover, setShowPopover] = useState(false);
  const progressRef = React.useRef<HTMLDivElement>(null);
  const [popoverPlacement, setPopoverPlacement] = useState<Placement>(
    Placement.Bottom,
  );

  const sequenceId = useSelector((state: RootState) =>
    isSubsequence ? state.sequence?.sequence?.id : null,
  );

  const linkTo = isSubsequence
    ? `/sequence/${sequenceId}/subsequence/${row.id}/conditions`
    : `/sequence/${row.id}/steps`;

  const hidePopper = () => {
    setShowPopover(false);
  };

  const handleClickAndScrollOutside = React.useCallback(
    (event: MouseEvent | Event) => {
      if (
        progressRef?.current &&
        !progressRef?.current?.contains(event.target as Node)
      ) {
        hidePopper();
      }
    },
    [hidePopper, progressRef],
  );

  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickAndScrollOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickAndScrollOutside);
    };
  }, [handleClickAndScrollOutside]);

  const handleTogglePopover = (toggle: boolean) => {
    if (toggle && progressRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const popoverHeight = 200;
      const margin = 20;
      const spaceBelow = windowHeight - rect.bottom;

      if (spaceBelow < popoverHeight + margin) {
        setPopoverPlacement(Placement.Top);
      } else {
        setPopoverPlacement(Placement.Bottom);
      }
    }
    setShowPopover(toggle);
  };
  return (
    <div className="sequence-name-wrapper">
      <OverlayTooltip text={name} placement={Placement.Bottom}>
        <Link
          id={ProductTour.VisitFirstSequence}
          className="text-container"
          to={linkTo}
        >
          {name}
        </Link>
      </OverlayTooltip>

      {row.subSequences?.length > 0 && (
        <OverlayPopover
          show={showPopover}
          placement={popoverPlacement}
          onToggle={handleTogglePopover}
          className="subsequence-popover"
          content={
            <SubSequencePopoverContent
              subSequences={row?.subSequences}
              sequenceId={row.id}
            />
          }
        >
          <OverlayTooltip text="Subsequences" placement={Placement.Right}>
            <div
              ref={progressRef}
              className={`sequence-subsequence-count ${
                showPopover ? 'subsequence-active' : ''
              }`}
              {...accessibleOnClick((e) => {
                e.preventDefault();
                e.stopPropagation();
                handleTogglePopover(!showPopover);
              })}
            >
              <span className="d-flex">
                <GitMerge width={16} height={16} color="#6B7280" />
                <span className="count">{row.subSequences?.length}</span>
              </span>
            </div>
          </OverlayTooltip>
        </OverlayPopover>
      )}
    </div>
  );
};

const sequenceStatus = (row, t) => {
  if (!row || !row.sequenceStatusData) {
    return null;
  }

  const {
    sequenceProgress,
    sequencepauseReason: sequencePauseReason,
  } = row.sequenceStatusData;

  const { variant, label } = getBadgeVariant(sequenceProgress, t);

  if (sequenceProgress === SequenceProgress.Pause) {
    const client = getAgencyClient();

    const renderPausedMessage = () => {
      const paused = (
        <p className="sequence-progress">{`${t('messages.paused')}`}</p>
      );

      const showOnlyThesePauseReasonsToClient = [
        SequencePauseReasons.EmailAccountDisconnected,
        SequencePauseReasons.EmailSendingLimitReached,
        SequencePauseReasons.EmailAccountDeleted,
      ];

      if (
        isEmpty(client) ||
        showOnlyThesePauseReasonsToClient.includes(sequencePauseReason)
      ) {
        const reason = sequencePauseMessages[sequencePauseReason];

        return (
          <OverlayTooltip
            text={t(`messages.${reason}`)}
            placement={Placement.Bottom}
            className="tooltip-xl"
          >
            {paused}
          </OverlayTooltip>
        );
      }

      return paused;
    };

    return (
      <div className="sequence-status">
        <div className="sequence-status-badge">
          <Badge className="badge-dot-danger" />
        </div>
        {renderPausedMessage()}
      </div>
    );
  }

  return <StatusBadge label={label} variant={variant} />;
};

export const SequenceNameField = ({
  name,
  row,
  isSubsequence = false,
}: SequenceNameProps) => {
  const { t } = useTranslation();

  const formatDate = (isoDateString: string) => {
    if (!isoDateString || !DateTime.fromISO(isoDateString).isValid) {
      return '';
    }

    const userTimeZone = getUserTimeZone() || 'UTC';
    return DateTime.fromISO(isoDateString, { zone: userTimeZone })?.toFormat(
      'dd MMM yyyy, hh:mm a',
    );
  };

  const { sequenceStatusData } = row || {};
  const { sequenceProgress } = sequenceStatusData || {};

  const isPaused = sequenceProgress === SequenceProgress.Pause;
  const pausedAtDate = row?.pausedAt ? formatDate(row?.pausedAt) : null;
  const createdAtDate = row?.sequenceCreatedAt
    ? formatDate(row?.sequenceCreatedAt)
    : null;

  return (
    <div>
      {SequenceNameJSX({ name, row, isSubsequence })}

      <div className="d-flex">
        {sequenceStatus(row, t)}

        <div className="sequence-steps-created-on">
          <span className="sequence-divider-vertical steps-divider">|</span>
          {`${row?.totalSteps} Steps`}{' '}
          {pausedAtDate && (
            <>
              <span className="sequence-divider-vertical">|</span>
              {`Paused at ${pausedAtDate}`}
            </>
          )}
          {!isPaused && createdAtDate && (
            <>
              <span className="sequence-divider-vertical">|</span>
              {`Created on ${createdAtDate}`}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
